/**
 * Focused hook for divisional chakra start point management
 * Handles start point state, chart settings read/write, and event emission
 */

import { devError } from '../utils/devLog';
import { useState, useCallback } from 'react';
import { useMultipleCharts } from '../components/common/AstrologyChart/MultipleChartsContext';
import { globalEventStream } from '../components/common/AstrologyChart/universal_architecture/core/EventStream';
import {
  DivisionalChakraType,
  ChakraStartPoint,
  getDivisionalChakraConfig
} from '../config/divisionalChakraConfig';

export interface ChakraStartPointState {
  startPoint: ChakraStartPoint;
  setStartPoint: React.Dispatch<React.SetStateAction<ChakraStartPoint>>;
  getStartPointSetting: () => ChakraStartPoint;
  updateStartPoint: (newStartPoint: ChakraStartPoint, showChakra: boolean) => Promise<void>;
}

/**
 * Hook for managing chakra start point for the active chart
 */
export const useChakraStartPoint = (
  chakraType: DivisionalChakraType,
  isLoading: boolean,
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>,
  setError: React.Dispatch<React.SetStateAction<string | null>>
): ChakraStartPointState => {
  const { activeChartId, getActiveChart, updateChart } = useMultipleCharts();
  const config = getDivisionalChakraConfig(chakraType);
  const startPointKey = `${chakraType}StartPoint` as const;

  const getStartPointSetting = useCallback((): ChakraStartPoint => {
    try {
      if (!activeChartId) {
        return config.defaultStartPoint;
      }

      const activeChart = getActiveChart();
      if (!activeChart || !activeChart.settings) {
        return config.defaultStartPoint;
      }

      const startPoint = (activeChart.settings as any)[startPointKey];
      if (!startPoint || typeof startPoint !== 'string') {
        return config.defaultStartPoint;
      }

      return startPoint as ChakraStartPoint;
    } catch (error) {
      devError(`useChakraStartPoint[${chakraType}]: Error getting start point:`, error);
      return config.defaultStartPoint;
    }
  }, [activeChartId, getActiveChart, startPointKey, config.defaultStartPoint, chakraType]);

  const [startPoint, setStartPoint] = useState<ChakraStartPoint>(() => {
    if (activeChartId) {
      return getStartPointSetting();
    }
    return config.defaultStartPoint;
  });

  const updateStartPoint = useCallback(async (
    newStartPoint: ChakraStartPoint,
    showChakra: boolean
  ): Promise<void> => {
    if (!activeChartId) {
      const errorMsg = `No active chart ID available for ${chakraType} start point update`;
      devError(`useChakraStartPoint[${chakraType}]:`, errorMsg);
      setError(errorMsg);
      return;
    }

    if (!updateChart) {
      const errorMsg = 'updateChart function not available';
      devError(`useChakraStartPoint[${chakraType}]:`, errorMsg);
      setError(errorMsg);
      return;
    }

    const currentStartPoint = getStartPointSetting();
    if (currentStartPoint === newStartPoint) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const activeChart = getActiveChart();
      if (!activeChart) {
        throw new Error('Active chart became unavailable during update');
      }

      await updateChart(activeChartId, {
        settings: {
          ...activeChart.settings,
          [startPointKey]: newStartPoint
        }
      });

      setStartPoint(newStartPoint);

      // Emit event to re-render chakra with new start point
      if (showChakra) {
        // Only emit if chakra is currently visible
        globalEventStream.emit({
          id: `${chakraType}-startpoint-${activeChartId}-${Date.now()}`,
          type: `${chakraType}.chakra.startpoint.change`,
          source: `divisional-chakra-settings-hook-${chakraType}`,
          timestamp: Date.now(),
          chartId: activeChartId,
          data: {
            chartId: activeChartId,
            startPoint: newStartPoint,
            [`show${chakraType.toUpperCase()}Chakra`]: showChakra
          }
        });
      }
    } catch (error) {
      const errorMsg = `Failed to update ${chakraType} start point: ${error}`;
      devError(`useChakraStartPoint[${chakraType}]:`, errorMsg);
      setError(errorMsg);
    } finally {
      setIsLoading(false);
    }
  }, [activeChartId, getActiveChart, updateChart, getStartPointSetting, startPointKey, chakraType, setIsLoading, setError]);

  return {
    startPoint,
    setStartPoint,
    getStartPointSetting,
    updateStartPoint
  };
};
